import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../config/axiosConfig";
import config from "../../config/api";

const BackendURL = config.backendUrl;

export const fetchOtherExpenses = createAsyncThunk(
  'otherExpenses/fetchAll',
  async (_, thunkAPI) => {
    try {
      const response = await axiosInstance.get(`${BackendURL}/otherexpenses`);
      return response.data; // returns an array
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data || 'Failed to fetch expenses');
    }
  }
);

export const createOtherExpense = createAsyncThunk(
  'otherExpenses/create',
  async (expenseData, thunkAPI) => {
    try {
      const response = await axiosInstance.post(`${BackendURL}/otherexpenses/create`, expenseData);
      return response.data; // contains { message, skipped }
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data || 'Failed to create expense');
    }
  }
);

export const updateOtherExpense = createAsyncThunk(
  'otherExpenses/update',
  async ({ id, data }, thunkAPI) => {
    try {
      const response = await axiosInstance.put(`${BackendURL}/otherexpenses/update/${id}`, data);
      return response.data; // contains { message, expense }
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data || 'Failed to update expense');
    }
  }
);

export const deleteOtherExpense = createAsyncThunk(
  'otherExpenses/delete',
  async (id, thunkAPI) => {
    try {
      await axiosInstance.delete(`${BackendURL}/otherexpenses/delete/${id}`);
      return id;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data || 'Failed to delete expense');
    }
  }
);

const otherExpensesSlice = createSlice({
  name: 'otherExpenses',
  initialState: {
    items: [],
    loading: false,
    error: null,
    message: null,   // ✅ to store success/info messages
  },
  reducers: {
    clearMessage: (state) => { state.message = null; }
  },
  extraReducers: (builder) => {
    builder

    .addCase(fetchOtherExpenses.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOtherExpenses.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload;
        state.items = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.data)
            ? payload.data
            : Array.isArray(payload?.expenses)
              ? payload.expenses
              : Array.isArray(payload?.items)
                ? payload.items
                : [];
      })
      .addCase(fetchOtherExpenses.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(createOtherExpense.pending, (state) => {
        state.loading = true;
      })
      .addCase(createOtherExpense.fulfilled, (state, action) => {
        state.loading = false;
        state.message = action.payload.message; // show success msg
      })
      .addCase(createOtherExpense.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(updateOtherExpense.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateOtherExpense.fulfilled, (state, action) => {
        state.loading = false;
        state.message = action.payload.message;
        const updated = action.payload.expense;
        if (updated) {
          const index = state.items.findIndex(item => item._id === updated._id);
          if (index !== -1) {
            state.items[index] = updated;
          }
        }
      })
      .addCase(updateOtherExpense.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(deleteOtherExpense.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteOtherExpense.fulfilled, (state, action) => {
        state.loading = false;
        state.items = state.items.filter(item => item._id !== action.payload);
      })
      .addCase(deleteOtherExpense.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const { clearMessage } = otherExpensesSlice.actions;
export default otherExpensesSlice.reducer;
